import { useEffect, useState } from 'react';

const STATUS_LABEL = {
  ahead: 'Surplus',
  on_track: 'On track',
  behind: 'Deficit',
  no_target: 'No target set',
};

export const formatPerformanceRupee = (amount) => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return '—';
  const sign = num < 0 ? '-' : '';
  return `${sign}₹${Math.abs(num).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatAsOfDate = (iso) => {
  const [year, month, day] = String(iso || '').split('-');
  if (!year || !month || !day) return '';
  return `${day}-${month}-${year}`;
};

export const BranchPerformanceTable = ({ row, asOf }) => {
  const variance = row?.variance == null ? null : Number(row.variance);
  const surplus = variance != null && variance >= 0;
  const lines = [
    ['Monthly Business Target', row?.monthly_target],
    ['Daily Business Target', row?.daily_target],
    ['Target Till Date', row?.target_till_date ?? row?.target],
    ['Achievement till Date', row?.achievement ?? row?.revenue],
  ];
  return (
    <div className="mt-2 overflow-hidden rounded-md border border-gray-300 text-[11px] leading-tight">
      <div className="border-b border-gray-300 bg-white px-2 py-1 font-semibold text-gray-900">
        {row?.name || 'Branch'}
        {asOf ? ` As of ${formatAsOfDate(asOf)}` : ''}
      </div>
      {lines.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[1.4fr_1fr] border-b border-gray-300">
          <div className="border-r border-gray-300 px-2 py-1 text-gray-800">{label}</div>
          <div className="px-2 py-1 text-right tabular-nums text-gray-900">
            {value == null || value === '' ? '—' : formatPerformanceRupee(value)}
          </div>
        </div>
      ))}
      <div className="grid grid-cols-[1.4fr_1fr]">
        <div className="border-r border-gray-300 bg-blue-800 px-2 py-1 font-semibold text-white">
          Variance (Surplus/Deficit)
        </div>
        <div className={`px-2 py-1 text-right font-semibold tabular-nums ${
          variance == null ? 'text-gray-500' : surplus ? 'text-green-700' : 'text-red-600'
        }`}
        >
          {variance == null ? '—' : formatPerformanceRupee(variance)}
        </div>
      </div>
    </div>
  );
};

export const TargetProgressBar = ({ percent }) => {
  const value = percent == null || percent === '' ? null : Number(percent);
  const [width, setWidth] = useState(0);
  const capped = value == null || Number.isNaN(value) ? 0 : Math.max(0, Math.min(value, 100));
  const tone = value == null
    ? 'bg-gray-300'
    : value >= 100
      ? 'bg-green-500'
      : value >= 70
        ? 'bg-blue-500'
        : 'bg-amber-500';

  useEffect(() => {
    const frame = requestAnimationFrame(() => setWidth(capped));
    return () => cancelAnimationFrame(frame);
  }, [capped]);

  return (
    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
      <div
        className={`h-full rounded-full ${tone} transition-all duration-700 ease-out`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
};

const LocationTargetBoard = ({
  rows = [],
  periodMonths = 1,
  usedCurrentMonth = false,
  asOf = '',
  canEdit = false,
  onSave,
  loading = false,
}) => {
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const save = async (row) => {
    if (!onSave) return;
    const raw = drafts[row.location_hub_id];
    const amount = raw == null || String(raw).trim() === '' ? null : raw;
    try {
      setSavingId(row.location_hub_id);
      await onSave(row.location_hub_id, amount);
      setDrafts((current) => {
        const next = { ...current };
        delete next[row.location_hub_id];
        return next;
      });
    } catch {
      // The caller shows the error. Keep the typed target so it can be saved again.
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-800">Branch performance</h2>
          <p className="text-xs text-gray-500">
            Daily target is the monthly target divided by 26 working days. Target till date
            multiplies that pace by the working days elapsed
            {periodMonths > 1 ? ` across ${periodMonths} months` : ''}. Sundays are off.
            {usedCurrentMonth ? ' This range has no dates, so the current month is shown.' : ''}
          </p>
        </div>
      </div>
      {loading && rows.length === 0 ? (
        <p className="text-sm text-gray-500">Loading location targets…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No location is available for this view.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((row) => {
            const editing = drafts[row.location_hub_id] != null;
            const draft = editing ? drafts[row.location_hub_id] : (row.monthly_target ?? '');
            return (
              <div key={row.location_hub_id} className="rounded-xl border border-gray-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-800 break-words">{row.name}</p>
                  <span className="text-[11px] font-semibold text-gray-500 shrink-0">
                    {STATUS_LABEL[row.status] || STATUS_LABEL.no_target}
                  </span>
                </div>
                <BranchPerformanceTable row={row} asOf={asOf} />
                <div className="mt-2">
                  <TargetProgressBar percent={row.progress_pct} />
                </div>
                <p className="mt-1 text-[11px] font-semibold text-gray-700">
                  {row.progress_pct == null ? '—' : `${row.progress_pct}% of target till date`}
                </p>
                {canEdit ? (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={draft}
                      onChange={(event) => setDrafts((current) => ({
                        ...current,
                        [row.location_hub_id]: event.target.value,
                      }))}
                      placeholder="Monthly target"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg"
                    />
                    <button
                      type="button"
                      disabled={savingId === row.location_hub_id}
                      onClick={() => save(row)}
                      className="shrink-0 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white disabled:opacity-60"
                    >
                      {savingId === row.location_hub_id ? 'Saving' : 'Save'}
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LocationTargetBoard;
